/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { useState, useCallback } from 'react'
import { connect } from 'react-redux'
import { Flex } from '../Layout'

import { Pager } from '../Pagination'
import { Mobile } from '../Responsive'
import ListHeader from './Header'
import { FIRST_CELL_WIDTH } from './list-constants'
import ListGroup from './ListGroup'
import ListRow from './Row'
import ListStatusView from './StatusView'
import { SelectInput } from '../ts-components/SelectInput'
import { Spin, Input } from 'antd'
import { SearchOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { paragraphMedium, weightRegular, weightSemiBold } from '../css'
import { paragraphSmallStyle } from 'src/pages/RegistrationInviteV2/steps/Main'

const List = ({
  headers,
  noItemsText,
  fullNoItemsText,
  options: _options,
  listProps,
  isInFrame,
  headerBorder,
  bgColor,
  hideHeaders = false,
  highlightSorted = false,
  localSort,
  forceMobile = false,
  showSearch = false,
  showSportFilter = false,
  shouldStickyHeader = false,
  removeOverflow = false,
  sportFilterOptions = [],
  setSportFilter,
  sportFilter,
  hideGroupName = false,
  hideStatusView = false,
  alternativeOptionsIcon = false,
  enabledAllItemsOption = false,
  displayPlaceholder = true,
  additionalFilters = null,
}) => {
  const {
    loading,
    error,
    items,
    groups,
    sort,
    setSort,
    direction,
    setDirection,
    getItems,
    pagination,
    getKey,
    searchTerm = '',
    setSearchTerm,
  } = listProps

  const options = isInFrame ? [] : _options

  const width = headers.reduce((collector, header, idx) => {
    return collector + (header.minWidth || (idx === 0 ? FIRST_CELL_WIDTH : 60))
  }, 90 + 16 + 12)

  const [initialDirection] = useState(direction)

  const isSearching = searchTerm.length > 0
  const hasItems = items.length > 0

  // Filter function for grouped select options
  const filterOptionGroup = useCallback((search, optionOrGroup) => {
    const isGroup = Array.isArray(optionOrGroup.options)
    if (isGroup) {
      return false
    }
    return optionOrGroup.label.toLowerCase().includes(search.toLowerCase())
  }, [])

  // Transform sportFilterOptions into grouped format for SelectInput
  const groupedSportOptions = useCallback(() => {
    const fullStatsOptions = sportFilterOptions
      ?.filter((option) => !option.is_generic)
      ?.map((option) => ({
        value: option.value,
        label: option.text.replace(/ \(Basic\)$/, ''),
        is_generic: false,
      }))
      ?.sort((a, b) => a.label.localeCompare(b.label))

    const basicStatsOptions = sportFilterOptions
      ?.filter((option) => option.is_generic)
      ?.map((option) => ({
        value: option.value,
        label: option.text.replace(/ \(Basic\)$/, ''),
        is_generic: true,
      }))
      ?.sort((a, b) => a.label.localeCompare(b.label))

    return [
      {
        label: (
          <div
            css={css`
              display: flex;
              flex-direction: row;
              align-items: center;
              justify-content: space-between;
            `}
          >
            <span
              css={css`
                ${paragraphSmallStyle}
                ${weightSemiBold}
                color: ${colors.PRIMARY};
                text-transform: uppercase;
              `}
            >
              Full Stats
            </span>
            <QuestionCircleOutlined
              style={{
                fontSize: 16,
                color: colors.PRIMARY,
                cursor: 'pointer',
              }}
              onClick={() => {
                window.open(
                  'https://support.sportninja.com/scoring-2-versions-basic-vs-full-stats',
                  '_blank'
                )
              }}
            />
          </div>
        ),
        options: fullStatsOptions || [],
      },
      {
        label: (
          <div
            css={css`
              display: flex;
              flex-direction: row;
              align-items: center;
              justify-content: space-between;
            `}
          >
            <span
              css={css`
                ${paragraphSmallStyle}
                ${weightSemiBold}
                color: ${colors.PRIMARY};
                text-transform: uppercase;
              `}
            >
              Basic Stats
            </span>
            <QuestionCircleOutlined
              style={{
                fontSize: 16,
                color: colors.PRIMARY,
                cursor: 'pointer',
              }}
              onClick={() => {
                window.open(
                  'https://support.sportninja.com/scoring-2-versions-basic-vs-full-stats',
                  '_blank'
                )
              }}
            />
          </div>
        ),
        options: basicStatsOptions || [],
      },
    ]
  }, [sportFilterOptions])

  return (
    <>
      {showSearch && (
        <Flex
          css={css`
            margin-top: 16px;
          `}
        >
          <Flex
            className='list-search-input-container'
            noFlex
            alignItems='center'
            justifyContent='space-between'
            css={css`
              display: flex;
              flex-direction: row;
              justify-content: flex-end;
              align-items: flex-end;
              @media (max-width: 768px) {
                max-width: 100vw;
                flex-direction: column;
                gap: 16px;
                align-items: flex-start;

                .suspension-select-container {
                  max-width: 100%;
                }

                .list-search-input {
                  width: 90vw;
                }

                .report-select {
                  width: 90vw;
                }
              }
            `}
          >
            <Input
              className='list-search-input'
              placeholder='Search'
              prefix={
                <SearchOutlined
                  css={css`
                    font-size: 16px;
                    color: ${colors.NEUTRAL_50};
                  `}
                />
              }
              suffix={loading && isSearching ? <Spin size='small' /> : null}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear={true}
              css={css`
                ${paragraphMedium}
                ${weightRegular}
                width: 300px;
                color: ${colors.WHITE};
                border-radius: 8px;
                background: transparent;
                height: 40px !important;
                margin-right: 16px;
                .ant-input {
                  background-color: transparent;
                  color: ${colors.WHITE};
                }
                .ant-input-prefix {
                  color: ${colors.NEUTRAL_100};
                }
                &::placeholder {
                  color: ${colors.NEUTRAL_100};
                }
                @media (max-width: 768px) {
                  width: 90vw;
                }
                // remove input hover background
                &:hover {
                  background-color: transparent;
                }
              `}
            />
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 16px;
              `}
            >
              {showSportFilter ? (
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 8px;
                    flex: 1 0 0;
                    @media (max-width: 768px) {
                      width: 100%;
                    }
                  `}
                >
                  <SelectInput
                    placeholder={displayPlaceholder ? '-' : 'Select Sport'}
                    label='Sport'
                    options={groupedSportOptions()}
                    filterOption={filterOptionGroup}
                    name='sport'
                    value={sportFilter || ''}
                    showSearch={true}
                    onChange={(value) => setSportFilter(value)}
                    fieldErrors={{}}
                    customError={''}
                    disabled={false}
                    allowClear={true}
                    style={css`
                      min-width: 200px;
                    `}
                    backgroundColor='transparent'
                  />
                </div>
              ) : null}
              {additionalFilters && additionalFilters()}
            </div>
          </Flex>
        </Flex>
      )}
      {loading && !hasItems && (
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 400px;
          `}
        >
          <Spin size='small' />
        </div>
      )}
      {error || (!loading && !hasItems) ? (
        <ListStatusView
          hideStatusView={hideStatusView}
          loading={loading}
          error={error}
          zeroItems={!hasItems}
          noItemsText={noItemsText}
          fullNoItemsText={
            searchTerm.length > 0
              ? `No results found for "${searchTerm}"`
              : fullNoItemsText
          }
        />
      ) : (
        hasItems && (
          <Mobile>
            {(isMobile) => {
              return (
                <div
                  className='list-container'
                  css={css`
                    position: relative;
                    ${removeOverflow && 'overflow: visible !important;'}

                    ${window.innerWidth - 32 < width &&
                    css`
                      &::after {
                        position: absolute;
                        top: 0;
                        bottom: 0;
                        right: 0px;
                        content: '';
                        width: 42px;
                        pointer-events: none;
                        background: linear-gradient(
                          to left,
                          ${isInFrame ? 'white' : 'rgba(0, 0, 0, 0.5)'},
                          ${isInFrame ? 'white 20%' : 'transparent 55%'},
                          transparent
                        );
                      }
                    `}
                  `}
                >
                  <div
                    className='list-inner'
                    css={css`
                      position: relative;
                      margin: 24px 8px ${pagination ? '100px' : '8px'} 8px;

                      ${(isMobile || forceMobile) &&
                      css`
                        overflow-x: auto;
                        margin-left: 0;
                        margin-right: 0;
                      `}
                    `}
                  >
                    {!hideHeaders && (
                      <ListHeader
                        alternativeOptionsIcon={alternativeOptionsIcon}
                        options={options}
                        headers={headers}
                        setSort={setSort}
                        setDirection={setDirection}
                        sort={sort}
                        direction={direction}
                        initialDirection={initialDirection}
                        headerBorder={headerBorder}
                        width={width}
                        isMobile={isMobile || forceMobile}
                        shouldStickyHeader={shouldStickyHeader}
                      />
                    )}
                    {groups
                      ? Object.keys(groups).map((groupId) => {
                          const group = groups[groupId]
                          return (
                            <ListGroup
                              key={groupId}
                              width={width}
                              group={group}
                              headers={headers}
                              options={options}
                              sort={sort}
                              direction={direction}
                              bgColor={bgColor}
                              highlightSorted={highlightSorted}
                              getKey={getKey}
                              localSort={localSort}
                              isMobile={isMobile || forceMobile}
                              hideGroupName={hideGroupName}
                            />
                          )
                        })
                      : items.map((item) => {
                          let key = item.id || item.uid
                          if (typeof getKey === 'function') {
                            key = getKey(item)
                          }

                          return (
                            <ListRow
                              alternativeOptionsIcon={alternativeOptionsIcon}
                              key={key}
                              item={item}
                              headers={headers}
                              options={options}
                              sort={sort}
                              bgColor={bgColor}
                              highlightSorted={highlightSorted}
                              width={width}
                              isMobile={isMobile || forceMobile}
                            />
                          )
                        })}
                    {pagination && (
                      <Pager
                        onClick={getItems}
                        page={pagination.current_page}
                        total={pagination.total_pages}
                      />
                    )}
                  </div>
                </div>
              )
            }}
          </Mobile>
        )
      )}
    </>
  )
}
const mapStateToProps = (state) => {
  return {
    isInFrame: state.auth.inFrame,
  }
}

export default connect(mapStateToProps)(List)
